import * as React from 'react';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import giggLogo from '../../../images/gigg-logo.svg';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { isUserAuthorized, setPageTitle } from '../../../utils/Utils';
import { useFormik } from 'formik';
import { useFinalizeAccount } from '../../../services/hooks/useUser';
import { confirmSignIn, signIn } from 'aws-amplify/auth';
import { environment } from '../../../environments/environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import variables from '../../../styles/variables.module.scss';
import './Login.scss';
import { MSG_PASSWORD_REQUIREMENT, REGEX_PASSWORD } from '../../../constants';
import { FormInstance } from 'antd';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

const Setup: React.FC = () => {
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const { mutate: finalizeAccount } = useFinalizeAccount();

  useEffect(() => {
    setPageTitle('Activate');

    // check if user is logged in
    (async () => {
      const isLoggedIn = await isUserAuthorized();
      if (isLoggedIn) {
        navigate('/');
      }
    })();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const code = query.get('code');
    const userName = query.get('userName');
    const email = decodeURIComponent(query.get('email') as string);
    if (!code || !userName || !email) {
      setError({ message: 'Missing required parameter' } as Error);
      return;
    }

    setCode(code);
    setUserName(userName);
    setEmail(email);
  }, []);

  const validatePassword = (rule: any, value: string, callback: Function, form: FormInstance) => {
    if (value && !form.isFieldValidating('confirmPassword')) {
      form.validateFields(['confirmPassword'], { dirty: true });
    }
    callback();
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const signInResults = await signIn({ username: email, password: code });
        if (signInResults?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          const confirmResults = await confirmSignIn({ challengeResponse: code });
          if (confirmResults?.isSignedIn) {
            await finalizeAccount({
              userName,
              userPoolId: environment.userPoolId,
              firstName: values.firstName,
              lastName: values.lastName,
            });
            navigate('/');
          }
        }
      } catch (e: any) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="flex-container justify-center router-wrapper">
      <div className="login-outer">
        <div className="flex-container justify-center">
          <div className="margin-bottom-md">
            <div className="login-image-container">
              <img className="login-img" src={giggLogo} height="auto" width="100%" alt="Gigg" />
            </div>
            <div className="flex-container justify-center">Finish setting up your account</div>
          </div>
        </div>
        <div className="login-inner">
          <Form layout="vertical" className="login-form" onFinish={formik.handleSubmit}>
            {error && error.message && (
              <Form.Item>
                <div style={{ color: variables.errorColor }} className="text-center">
                  {error.message}
                </div>
              </Form.Item>
            )}
            <Form.Item name="firstName" rules={[{ required: true, message: 'Please input your first name' }]}>
              <Input
                name="firstName"
                className="login-input"
                placeholder="First Name"
                onChange={formik.handleChange}
                value={formik.values.firstName}
              />
            </Form.Item>
            <Form.Item name="lastName" rules={[{ required: true, message: 'Please input your Last Name' }]}>
              <Input
                name="lastName"
                className="login-input"
                placeholder="Last Name"
                onChange={formik.handleChange}
                value={formik.values.lastName}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password' },
                { pattern: new RegExp(REGEX_PASSWORD), message: MSG_PASSWORD_REQUIREMENT },
                (formInstance: FormInstance) => ({
                  validator: (rule, value, callback) => validatePassword(rule, value, callback, formInstance),
                }),
              ]}
            >
              <Input
                name="password"
                className="login-input"
                type="password"
                placeholder="Password"
                onChange={formik.handleChange}
                value={formik.values.password}
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              rules={[
                { required: true, message: 'Please confirm your password' },
                { pattern: new RegExp(REGEX_PASSWORD), message: MSG_PASSWORD_REQUIREMENT },
                (formInstance: FormInstance) => ({
                  validator: (rule, value, callback) => validatePassword(rule, value, callback, formInstance),
                }),
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The new password that you entered do not match!'));
                  },
                }),
              ]}
            >
              <Input
                name="confirmPassword"
                className="login-input"
                type="password"
                placeholder="Confirm Password"
                onChange={formik.handleChange}
                value={formik.values.confirmPassword}
              />
            </Form.Item>
            <div className="flex-container margin-bottom-md">
              <div className="flex-container flex">
                <Button type="primary" htmlType="submit" className="login-button">
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinnerThird} size="lg" className="fa-fast-spin" />
                  ) : (
                    <span>Activate</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex-container justify-space-between">
              <Link to="/login" className="login-link">
                Sign In
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Setup;
